import { create } from 'zustand';
import { salesTeamsApi } from '../lib/api';
import type {
  SalesTeamSummary,
  SalesTeamDetail,
  CreateSalesTeamInput,
  UpdateSalesTeamInput,
  SetMembersInput,
} from '../types/salesTeam';

/** The default team a viewer lands on: their first managed team, else null (All Teams). */
export function resolveDefaultTeamId(mine: SalesTeamSummary[]): string | null {
  if (mine.length === 0) return null;
  const sorted = [...mine].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
  return sorted[0].id;
}

interface SalesTeamsState {
  teams: SalesTeamSummary[];
  mine: SalesTeamSummary[];
  loading: boolean;
  error: string | null;
  loadAll: () => Promise<void>;
  loadMine: () => Promise<void>;
  create: (input: CreateSalesTeamInput) => Promise<SalesTeamDetail>;
  update: (id: string, input: UpdateSalesTeamInput) => Promise<SalesTeamDetail>;
  remove: (id: string) => Promise<void>;
  getOne: (id: string) => Promise<SalesTeamDetail>;
  setMembers: (id: string, input: SetMembersInput) => Promise<void>;
}

export const useSalesTeamsStore = create<SalesTeamsState>((set, get) => ({
  teams: [],
  mine: [],
  loading: false,
  error: null,
  loadAll: async () => {
    set({ loading: true, error: null });
    try {
      const { salesTeams } = await salesTeamsApi.list();
      set({ teams: salesTeams, loading: false });
    } catch (e) {
      set({ loading: false, error: e instanceof Error ? e.message : 'Failed to load teams' });
    }
  },
  loadMine: async () => {
    const { salesTeams } = await salesTeamsApi.mine();
    set({ mine: salesTeams });
  },
  create: async (input) => {
    const { salesTeam } = await salesTeamsApi.create(input);
    await get().loadAll();
    return salesTeam;
  },
  update: async (id, input) => {
    const { salesTeam } = await salesTeamsApi.update(id, input);
    await get().loadAll();
    return salesTeam;
  },
  remove: async (id) => {
    await salesTeamsApi.remove(id);
    await get().loadAll();
  },
  getOne: async (id) => {
    const { salesTeam } = await salesTeamsApi.get(id);
    return salesTeam;
  },
  setMembers: async (id, input) => {
    await salesTeamsApi.setMembers(id, input);
    await get().loadAll();
  },
}));
